import apiClient from './apiClient';
import { cachedGet } from './sessionConfigCache';

// ==================== Events ====================

export const eventAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/events', { params });
    return response.data;
  },

  // Get past events for dashboard
  getPastEvents: async (params = {}) => {
    const response = await apiClient.get('/events/past', { params });
    return response.data;
  },

  // Get revenue analytics (MTD, YTD, ALL TIME)
  getRevenueAnalytics: async () => {
    const response = await apiClient.get('/events/analytics/revenue');
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/events/${id}`, {
      timeout: 15000 // 15 second timeout
    });
    return response.data;
  },

  create: async (eventData) => {
    // _noRetry: a timed-out create may have succeeded server-side — never
    // auto-retry, or we'd create a duplicate event.
    const response = await apiClient.post('/events', eventData, { _noRetry: true });
    return response.data;
  },

  update: async (id, eventData) => {
    const response = await apiClient.put(`/events/${id}`, eventData);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/events/${id}`);
    return response.data;
  },

  markComplete: async (id) => {
    const response = await apiClient.patch(`/events/${id}/complete`);
    return response.data;
  },

  reopenEvent: async (id) => {
    const response = await apiClient.patch(`/events/${id}/reopen`);
    return response.data;
  },

  assignStaff: async (eventId, staffIds) => {
    const response = await apiClient.post(`/events/${eventId}/assign-staff`, {
      staffIds,
    });
    return response.data;
  },

  // Ensure the manual event has a pipeline-side EVT-xxxx shell so the
  // Event Details modal can render the unified workspace <StaffingEditor>
  // (Phase 3 of the Staffing unification). Idempotent server-side.
  ensurePipelineLink: async (eventId) => {
    const response = await apiClient.post(`/events/${eventId}/ensure-pipeline-link`);
    return response.data; // { linkedEventId, existed }
  },

  unassignStaff: async (eventId, staffId) => {
    const response = await apiClient.delete(
      `/events/${eventId}/unassign-staff/${staffId}`
    );
    return response.data;
  },

  // Budget APIs
  getBudget: async (eventId) => {
    const response = await apiClient.get(`/events/${eventId}/budget`);
    return response.data;
  },

  getAllBudgets: async () => {
    const response = await apiClient.get('/budgets');
    return response.data;
  },

  // Update staff pay rates for an event (from budget page)
  updateStaffPay: async (eventId, staffPay) => {
    const response = await apiClient.put(`/events/${eventId}/staff-pay`, { staffPay });
    return response.data;
  },

  // Sync all staff earnings from budget data
  syncStaffEarnings: async () => {
    const response = await apiClient.post('/events/sync-staff-earnings');
    return response.data;
  },

  // Document upload
  uploadDocument: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/events/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Notify staff about a document
  notifyStaffAboutDocument: async (eventId, data) => {
    const response = await apiClient.post(`/events/${eventId}/documents/notify`, data);
    return response.data;
  },

  // Add document to control center
  addDocumentToControlCenter: async (eventId, data) => {
    const response = await apiClient.post(`/events/${eventId}/control-center/document`, data);
    return response.data;
  },
};

// ==================== Shift Assignments API ====================

export const shiftAPI = {
  getAssignments: async (params = {}) => {
    const response = await apiClient.get('/shifts/assignments', { params });
    return response.data;
  },

  createAssignment: async (assignmentData) => {
    const response = await apiClient.post('/shifts/assignments', assignmentData);
    return response.data;
  },

  confirmShift: async (assignmentId, status, declineReason = null) => {
    const response = await apiClient.put(`/shifts/assignments/${assignmentId}/confirm`, {
      status,
      declineReason
    });
    return response.data;
  },

  getPendingShifts: async (staffId) => {
    const response = await apiClient.get(`/shifts/pending/${staffId}`);
    return response.data;
  },

  getConfirmationStatus: async (eventId) => {
    const response = await apiClient.get(`/shifts/event/${eventId}/confirmation-status`);
    return response.data;
  },
};

// ==================== Clients ====================

export const clientAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/clients', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/clients/${id}`);
    return response.data;
  },

  create: async (clientData) => {
    const response = await apiClient.post('/clients', clientData);
    return response.data;
  },

  update: async (id, clientData) => {
    const response = await apiClient.put(`/clients/${id}`, clientData);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/clients/${id}`);
    return response.data;
  },
};

// ==================== Locations ====================

export const locationAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/locations', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/locations/${id}`);
    return response.data;
  },

  create: async (locationData) => {
    const response = await apiClient.post('/locations', locationData);
    return response.data;
  },

  update: async (id, locationData) => {
    const response = await apiClient.put(`/locations/${id}`, locationData);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/locations/${id}`);
    return response.data;
  },

  backfillCoordinates: async (dryRun = false) => {
    const response = await apiClient.post(`/locations/backfill-coordinates?dry_run=${dryRun}`, {});
    return response.data;
  },
};

// ==================== Company API ====================

export const companyAPI = {
  getSettings: async () => cachedGet('company/settings', async () => {
    const response = await apiClient.get('/company/settings');
    return response.data;
  }),

  updateSettings: async (settingsData) => {
    const response = await apiClient.put('/company/settings', settingsData);
    return response.data;
  },

  getDocuments: async () => {
    const response = await apiClient.get('/company/documents');
    return response.data;
  },

  addDocument: async (docData) => {
    const response = await apiClient.post('/company/documents', docData);
    return response.data;
  },

  deleteDocument: async (docId) => {
    const response = await apiClient.delete(`/company/documents/${docId}`);
    return response.data;
  },

  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/upload/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ==================== Event Vendors (Preferred Vendors for Events) ====================

export const eventVendorAPI = {
  // Get all preferred vendors
  getAll: async (params = {}) => {
    const response = await apiClient.get('/event-vendors', { params });
    return response.data;
  },

  // Get a specific preferred vendor
  getById: async (id) => {
    const response = await apiClient.get(`/event-vendors/${id}`);
    return response.data;
  },

  // Create a new preferred vendor
  create: async (vendorData) => {
    const response = await apiClient.post('/event-vendors', vendorData);
    return response.data;
  },

  // Update a preferred vendor
  update: async (id, vendorData) => {
    const response = await apiClient.put(`/event-vendors/${id}`, vendorData);
    return response.data;
  },

  // Delete a preferred vendor
  delete: async (id) => {
    const response = await apiClient.delete(`/event-vendors/${id}`);
    return response.data;
  },

  // Assign vendor to an event
  assignToEvent: async (eventId, vendorData) => {
    const response = await apiClient.post(`/event-vendors/event/${eventId}/assign`, vendorData);
    return response.data;
  },

  // Unassign vendor from an event
  unassignFromEvent: async (eventId, vendorId) => {
    const response = await apiClient.delete(`/event-vendors/event/${eventId}/unassign/${vendorId}`);
    return response.data;
  },

  // Get vendors assigned to an event
  getEventVendors: async (eventId) => {
    const response = await apiClient.get(`/event-vendors/event/${eventId}`);
    return response.data;
  },

  // Update ONE assigned vendor (call time / pay rate / notes) on ONE event.
  // Hits the positional PATCH so the vendor array is never rewritten. An
  // explicit null value clears that field (backend $unset); an absent key
  // leaves it untouched.
  patchVendor: async (eventId, vendorId, patch) => {
    const response = await apiClient.patch(`/event-vendors/event/${eventId}/vendor/${vendorId}`, patch);
    return response.data;
  },
};

// ==================== Checklist API ====================

export const checklistAPI = {
  ensureDefaults: async (eventId) => {
    const response = await apiClient.post(`/events/${eventId}/checklist/ensure-defaults`);
    return response.data;
  },
  getTemplates: async () => {
    const response = await apiClient.get('/events/my-checklist-templates');
    return response.data;
  },
  // Reset an event's checklist to the tenant's Standard Checklist
  // (company_settings.checklistDefault, else the seed). Replaces the old
  // broken built-in-template path (Deploy A/B).
  applyStandard: async (eventId) => {
    const response = await apiClient.post(`/events/${eventId}/checklist/apply-standard`);
    return response.data;
  },
  toggleItem: async (eventId, itemIndex) => {
    const response = await apiClient.patch(`/events/${eventId}/checklist/${itemIndex}/toggle`);
    return response.data;
  },
  addItem: async (eventId, text, category) => {
    const response = await apiClient.post(`/events/${eventId}/checklist/item`, { text, category });
    return response.data;
  },
  deleteItem: async (eventId, itemIndex) => {
    const response = await apiClient.delete(`/events/${eventId}/checklist/${itemIndex}`);
    return response.data;
  },
  reset: async (eventId) => {
    const response = await apiClient.post(`/events/${eventId}/checklist/reset`);
    return response.data;
  },
  saveAsTemplate: async (eventId, name) => {
    const response = await apiClient.post(`/events/${eventId}/checklist/save-as-template`, { name });
    return response.data;
  },
  applyCustomTemplate: async (eventId, templateId) => {
    const response = await apiClient.post(`/events/${eventId}/checklist/apply-custom-template/${templateId}`, { replace: false });
    return response.data;
  },
  deleteTemplate: async (templateId) => {
    const response = await apiClient.delete(`/events/my-checklist-templates/${templateId}`);
    return response.data;
  },
};
